/* ScidLogComponent */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Prop, State, Watch, h } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import ScidLogoTemplate from './cwc-scid-logo.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import UtilCref from '@cewe-designsystem/util_cref';

/**
 * interface for scid as key and image path as string
 */
interface ScidLogosObject {
  [scid: string]: string;
}

@Component({
  tag: 'cwc-scid-logo',
  styleUrl: './cwc-scid-logo.scss',
  shadow: true,
})
export class ScidLogoComponent {
  /**
   * The default SCID to be used if no SCID could be determined.
   * @private
   */
  private readonly DEFAULT_SCID = 'default';

  /**
   * element
   */
  @Element() element: HTMLElement;

  /**
   * Current logo path chosen by scid cookie
   */
  currentLogoPath: string;

  /**
   * Utility for handling with the CREF.
   *
   * @type {UtilCref}
   */
  private readonly crefUtil: UtilCref;

  /**
   * Default logo to be used if nothing is found
   *
   * @type {string}
   */
  @Prop({ reflect: true })
  defaultLogo: string = '';

  /**
   * The object of logos with the scid as key
   *
   * @docs
   * @property {string} json
   */
  @Prop({ reflect: true })
  json: string;

  /**
   * The link for the logo
   *
   * @docs
   * @property {string} link
   */
  @Prop({ reflect: true })
  link: string;

  /**
   * The current site's keyAccount.
   *
   * @docs
   * @property {string} keyAccount
   */
  @Prop({ attribute: 'keyaccount', reflect: true })
  keyAccount: string = '';

  /**
   * The internal object of logos with typed and structured data
   *
   * @property {ScidLogosObject} jsonInternal
   */
  @State()
  jsonInternal: ScidLogosObject;

  /**
   * @constructor
   */
  constructor() {
    this.crefUtil = new UtilCref(this.keyAccount);
  }

  /**
   * Watch the prop as string to detect changes and map it to the typed variable
   *
   * @property {string} newValue
   */
  @Watch('json')
  parseInternalJsonProp(newValue: string) {
    if (newValue) this.jsonInternal = JSON.parse(newValue);
  }

  /**
   * Try to get the scid value from the url or cookie.
   * If no scid value is found in the url or cookies, the default scid value is returned.
   *
   * @return {string}
   */
  getScid() {
    let scid = this.DEFAULT_SCID;
    try {
      scid = this.crefUtil.getScid() || this.DEFAULT_SCID;
    } catch (e) {
      console.error('can not detect scid from cref', e);
    }
    return scid;
  }

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    this.parseInternalJsonProp(this.json);
  }

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {
    const _scid = this.getScid();
    this.currentLogoPath = this.jsonInternal[_scid] || this.defaultLogo;
  }

  componentDidRender() {
    if (this.currentLogoPath != '') {
      this.element.classList.remove('empty');
    }
  }

  /**
   * @inheritDoc
   * @TODO: Currently this empty method is required to make the Slot decorator work
   */
  componentWillUpdate() {}

  /**
   * render
   */
  render() {
    if (this.currentLogoPath == '') {
      return '';
    }

    return <ScidLogoTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(ScidLogoComponent.prototype, AbstractComponent.prototype);

export interface ScidLogoComponent extends AbstractComponent {}
